/**
 * PATH: apps/qa/src/config/tabs.ts
 * PURPOSE: QA Hub tab configuration for the shared tab system.
 */

import { createTabStore, type TabNavigationConfig } from '@quotech/ui'
import {
  LayoutDashboard,
  ClipboardList,
  FileStack,
  AlertTriangle,
  BarChart3,
  Camera,
  CheckCircle,
  Sparkles,
  History,
  MessageCircle,
  Globe,
} from 'lucide-react'

// ─── Tab Store ──────────────────────────────────────────────
export const useQaTabStore = createTabStore({
  hubId: 'qa',
  defaultTab: { path: '/records', title: 'Records', iconKey: 'ClipboardList' },
})

// ─── Icon Map ───────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  ClipboardList,
  FileStack,
  AlertTriangle,
  BarChart3,
  Camera,
  CheckCircle,
  Sparkles,
  History,
  MessageCircle,
  Globe,
}

// ─── Route Map ──────────────────────────────────────────────
const ROUTE_MAP: Record<string, { title: string; iconKey: string }> = {
  '/dashboard': { title: 'Dashboard', iconKey: 'LayoutDashboard' },
  '/records': { title: 'Records', iconKey: 'ClipboardList' },
  '/templates': { title: 'Templates', iconKey: 'FileStack' },
  '/ncrs': { title: 'NCRs', iconKey: 'AlertTriangle' },
  '/reports': { title: 'Reports', iconKey: 'BarChart3' },
  '/evidence': { title: 'Evidence', iconKey: 'Camera' },
  '/signoffs': { title: 'Sign-offs', iconKey: 'CheckCircle' },
  '/ai': { title: 'AI Insights', iconKey: 'Sparkles' },
  '/audit': { title: 'Audit Log', iconKey: 'History' },
  '/chat': { title: 'Chat', iconKey: 'MessageCircle' },
  '/portal': { title: 'Portal', iconKey: 'Globe' },
}

// ─── Config ─────────────────────────────────────────────────
export const QA_TAB_CONFIG: TabNavigationConfig = {
  useStore: useQaTabStore,
  routeMap: ROUTE_MAP,
  dynamicRoutes: [
    {
      pattern: /^\/records\/(?!new)(.+)/,
      getMetadata: (path) => ({
        title: `Record #${path.split('/')[2]}`,
        iconKey: 'ClipboardList',
      }),
    },
    {
      pattern: /^\/templates\/(?!new)(.+)/,
      getMetadata: (path) => ({
        title: `Template #${path.split('/')[2]}`,
        iconKey: 'FileStack',
      }),
    },
    {
      pattern: /^\/ncrs\/(?!new)(.+)/,
      getMetadata: (path) => ({
        title: `NCR #${path.split('/')[2]}`,
        iconKey: 'AlertTriangle',
      }),
    },
    {
      pattern: /^\/reports\/draft\/(.+)/,
      getMetadata: (path) => ({
        title: `Report #${path.split('/')[3]}`,
        iconKey: 'BarChart3',
      }),
    },
  ],
  excludedPaths: ['/sso', '/login', '/settings'],
  defaultNewTabPath: '/records',
  iconMap: ICON_MAP,
}
